import { apiCall } from '../services/apiUtil';

/**
 * Financial Transaction API for Group Expenses and Contributions
 */
export const financialTransactionAPI = {
  /**
   * Create a group expense (Debit transaction)
   * @param {object} expenseData - Expense data
   * @param {string} expenseData.groupId - Group UUID
   * @param {string} expenseData.description - Expense description
   * @param {number} expenseData.amount - Expense amount
   * @param {string} expenseData.category - Expense category
   * @param {number} expenseData.createdBy - User ID who created the expense
   * @returns {Promise<object>} Created expense
   */
  createExpense: async (expenseData) => {
    // Step 1: Create the financial transaction summary (Debit record for the expense)
    const payload = {
      type: 'Debit',
      useBy: expenseData.groupId,
      useByType: 'Group',
      totalAmount: expenseData.amount,
      title: expenseData.category || 'General',
      description: expenseData.description,
      status: 'pending',
      season: expenseData.season || 'Year',
      seasonYear: expenseData.seasonYear || new Date().getFullYear(),
    };

    const summary = await apiCall('/financial-transactions', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    const creatorId = expenseData.createdBy;
    const defaultDueDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

    if (expenseData.customSplits && expenseData.customSplits.length > 0) {
      await apiCall('/credit-transactions/bulk', {
        method: 'POST',
        body: JSON.stringify({
          ftsId: summary.id,
          userTransactions: expenseData.customSplits.map(s => {
            const isCreator = s.userId === creatorId;
            return {
              userId: s.userId,
              toUserId: creatorId,
              amount: s.amount,
              status: isCreator ? 'paid' : 'pending',
              paid_amount: isCreator ? s.amount : undefined,
              dueDate: defaultDueDate,
            };
          }),
        }),
      });
    } else if (expenseData.payers && expenseData.payers.length > 0) {
      const amountPerPerson = expenseData.amount / expenseData.payers.length;
      await apiCall('/credit-transactions/bulk', {
        method: 'POST',
        body: JSON.stringify({
          ftsId: summary.id,
          userTransactions: expenseData.payers.map(userId => {
            const isCreator = userId === creatorId;
            return {
              userId,
              toUserId: creatorId,
              amount: amountPerPerson,
              status: isCreator ? 'paid' : 'pending',
              paid_amount: isCreator ? amountPerPerson : undefined,
              dueDate: defaultDueDate,
            };
          }),
        }),
      });
    }

    return summary;
  },

  /**
   * Create a group contribution (Credit transaction summary + individual credit transactions)
   * @param {object} contributionData - Contribution data
   * @param {string} contributionData.groupId - Group UUID
   * @param {string} contributionData.description - Contribution description
   * @param {number} contributionData.totalAmount - Total contribution amount
   * @param {Array<{userId: number, amount: number}>} contributionData.userTransactions - Array of user transactions
   * @param {string} contributionData.dueDate - Due date for payments (ISO string)
   * @param {number} contributionData.createdBy - User ID who created the contribution
   * @returns {Promise<object>} Created contribution summary with credit transactions
   */
  createContribution: async (contributionData) => {
    // Step 1: Create the financial transaction summary (parent record)
    const summaryPayload = {
      type: 'Credit',
      useBy: contributionData.groupId,
      useByType: 'Group',
      totalAmount: contributionData.totalAmount,
      title: contributionData.description,
      description: contributionData.description,
      status: 'pending',
    };

    const summary = await apiCall('/financial-transactions', {
      method: 'POST',
      body: JSON.stringify(summaryPayload),
    });

    // Step 2: Create individual credit transactions for each member
    const creditTransactionsPayload = {
      ftsId: summary.id,
      userTransactions: contributionData.userTransactions.map(ut => ({
        userId: ut.userId,
        amount: ut.amount,
        status: 'pending',
        dueDate: contributionData.dueDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // Default 30 days
      })),
    };

    await apiCall('/credit-transactions/bulk', {
      method: 'POST',
      body: JSON.stringify(creditTransactionsPayload),
    });

    // Return the summary with the ftsId for reference
    return summary;
  },

  /**
   * Get financial transactions by group ID
   * @param {string} groupId - Group UUID
   * @param {object} filters - Optional filters
   * @param {string} filters.type - Transaction type ('Debit' or 'Credit')
   * @param {string} filters.status - Transaction status
   * @returns {Promise<Array>} List of transactions
   */
  getByGroupId: async (groupId, filters = {}) => {
    const allFilters = { useBy: groupId, useByType: 'Group', ...filters };
    const params = new URLSearchParams(
      Object.fromEntries(Object.entries(allFilters).filter(([, v]) => v != null))
    );
    return apiCall(`/financial-transactions?${params.toString()}`);
  },

  /**
   * Get expenses for a group (Debit transactions)
   * @param {string} groupId - Group UUID
   * @returns {Promise<Array>} List of expenses
   */
  getExpenses: async (groupId) => {
    return financialTransactionAPI.getByGroupId(groupId, { type: 'Debit' });
  },

  /**
   * Get contributions for a group (Credit transaction summaries with individual transactions)
   * @param {string} groupId - Group UUID
   * @returns {Promise<Array>} List of contribution summaries with creditTransactions array
   */
  getContributions: async (groupId) => {
    // This will return transaction summaries with embedded creditTransactions array
    // Each creditTransaction will include user details (firstName, lastName, fullName, email)
    return financialTransactionAPI.getByGroupId(groupId, { type: 'Credit' });
  },

  /**
   * Update transaction status
   * @param {number} transactionId - Transaction ID
   * @param {string} status - New status
   * @returns {Promise<object>} Updated transaction
   */
  updateStatus: async (transactionId, status) => {
    return apiCall(`/financial-transactions/${transactionId}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  },

  /**
   * Update an existing expense — patches the summary then replaces credit transactions
   * @param {number} expenseId - Summary ID
   * @param {object} expenseData - Same shape as createExpense
   * @param {number[]} existingCreditTransactionIds - IDs to delete before recreating
   */
  updateExpense: async (expenseId, expenseData, existingCreditTransactionIds) => {
    // Step 1: Patch the summary fields
    await apiCall(`/financial-transactions/${expenseId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        title: expenseData.category,
        totalAmount: expenseData.amount,
        season: expenseData.season,
        seasonYear: expenseData.seasonYear,
      }),
    });

    // Step 2: Delete existing credit transactions
    await Promise.all(
      existingCreditTransactionIds.map(id =>
        apiCall(`/credit-transactions/${id}`, { method: 'DELETE' })
      )
    );

    // Step 3: Recreate credit transactions (same logic as createExpense)
    const creatorId = expenseData.createdBy;
    const defaultDueDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

    if (expenseData.customSplits && expenseData.customSplits.length > 0) {
      await apiCall('/credit-transactions/bulk', {
        method: 'POST',
        body: JSON.stringify({
          ftsId: expenseId,
          userTransactions: expenseData.customSplits.map(s => {
            const isCreator = s.userId === creatorId;
            return {
              userId: s.userId,
              toUserId: creatorId,
              amount: s.amount,
              status: isCreator ? 'paid' : 'pending',
              paid_amount: isCreator ? s.amount : undefined,
              dueDate: defaultDueDate,
            };
          }),
        }),
      });
    } else if (expenseData.payers && expenseData.payers.length > 0) {
      const amountPerPerson = expenseData.amount / expenseData.payers.length;
      await apiCall('/credit-transactions/bulk', {
        method: 'POST',
        body: JSON.stringify({
          ftsId: expenseId,
          userTransactions: expenseData.payers.map(userId => {
            const isCreator = userId === creatorId;
            return {
              userId,
              toUserId: creatorId,
              amount: amountPerPerson,
              status: isCreator ? 'paid' : 'pending',
              paid_amount: isCreator ? amountPerPerson : undefined,
              dueDate: defaultDueDate,
            };
          }),
        }),
      });
    }
  },

  /**
   * Record a donation to the group pool.
   * donorUserId = null for anonymous / external donors.
   */
  createDonation: async ({ groupId, adminId, amount, donorUserId, donorName, note, season, seasonYear }) => {
    const title = donorName ? `Donation from ${donorName}` : 'Anonymous Donation';
    const summary = await apiCall('/financial-transactions', {
      method: 'POST',
      body: JSON.stringify({
        type: 'donation',
        useBy: groupId,
        useByType: 'Group',
        totalAmount: amount,
        title,
        description: note || null,
        status: 'completed',
        season: season || 'Year',
        seasonYear: seasonYear || new Date().getFullYear(),
      }),
    });

    // CT: donor → admin (toUserId=admin so it counts toward pool surplus)
    await apiCall('/credit-transactions/bulk', {
      method: 'POST',
      body: JSON.stringify({
        ftsId: summary.id,
        userTransactions: [{
          userId: donorUserId || null,
          toUserId: adminId,
          amount,
          status: 'paid',
          dueDate: new Date().toISOString(),
        }],
      }),
    });

    return summary;
  },

  /**
   * Record a group expense (spending from pool — admin only).
   * Creates a Debit FTS + one CT for the expense manager with toUserId=null (paid from pool).
   */
  createGroupExpense: async ({ groupId, title, amount, season, seasonYear, expenseManagerId }) => {
    const summary = await apiCall('/financial-transactions', {
      method: 'POST',
      body: JSON.stringify({
        type: 'Debit',
        useBy: groupId,
        useByType: 'Group',
        totalAmount: amount,
        title,
        status: 'completed',
        season: season || 'Year',
        seasonYear: seasonYear || new Date().getFullYear(),
      }),
    });

    // Record that the expense manager paid from the pool (toUserId null = pool source)
    await apiCall('/credit-transactions/bulk', {
      method: 'POST',
      body: JSON.stringify({
        ftsId: summary.id,
        userTransactions: [{
          userId: expenseManagerId,
          toUserId: null,
          amount,
          status: 'paid',
          dueDate: new Date().toISOString(),
        }],
      }),
    });

    return summary;
  },

  /**
   * Record a payment against a credit transaction
   */
  createPayment: async (paymentData) => {
    return apiCall('/payments', {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
  },

  /**
   * Delete a transaction
   * @param {number} transactionId - Transaction ID
   * @returns {Promise<object>} Deletion result
   */
  deleteTransaction: async (transactionId) => {
    return apiCall(`/financial-transactions/${transactionId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Get balances for a group/entity (generic, supports season filter)
   */
  getBalances: async ({ useBy, useByType, season, seasonYear } = {}) => {
    const params = new URLSearchParams(
      Object.fromEntries(
        Object.entries({ useBy, useByType, season, seasonYear }).filter(([, v]) => v != null)
      )
    );
    return apiCall(`/balances?${params}`);
  },

  /**
   * Get user balances (what they owe and are owed)
   */
  getUserBalances: async (userId, groupId = null) => {
    const url = groupId
      ? `/balances/user/${userId}?groupId=${groupId}`
      : `/balances/user/${userId}`;
    return apiCall(url);
  },

  /**
   * Create a poker settlement from game results
   */
  createPokerSettlement: async (eventId, groupId, gameTitle, playerResults) => {
    return apiCall('/settlements/poker', {
      method: 'POST',
      body: JSON.stringify({ eventId, groupId, gameTitle, playerResults }),
    });
  },

  /**
   * Get settlement for an event
   */
  getSettlementByEventId: async (eventId) => {
    return apiCall(`/settlements/event/${eventId}`);
  },

  /**
   * Confirm a payment (by the recipient)
   */
  confirmPayment: async (paymentId) => {
    return apiCall(`/payments/${paymentId}/confirm`, { method: 'POST' });
  },

  /**
   * Dispute a payment
   */
  disputePayment: async (paymentId, notes) => {
    return apiCall(`/payments/${paymentId}/dispute`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  },

  /**
   * Get all payments for a credit transaction
   */
  getPayments: async (creditTransactionId) => {
    return apiCall(`/payments?creditTransactionId=${creditTransactionId}`);
  },
};

export default financialTransactionAPI;
